<template>
    <!--
      Organization modal — create / view / edit. Migrated off the Bootstrap modal
      onto the shared BaseModal shell (overlay + box + motion + a11y), mirroring
      UsersModal / AgreementsModal. The three modes are views of one modal: the
      header title and footer actions swap with the mode, and the body cross-fades
      between the form and the read view. The image cropper is its own BaseModal
      layered on top. Controlled errors (validation, duplicates) render inline —
      per-field for missing inputs, and a label above the footer for business
      errors; uncontrolled failures surface as toasts via ErrorService. Colors are
      inlined (the BaseModal family inherits no shell tokens).
    -->
    <BaseModal
        :show="visible"
        @update:show="OnUpdateShow"
        :closable="true"
        :dismissible="view"
        :ariaLabel="header"
        :maxWidth="560">

        <template #header>
            <span class="om-head__title">{{ header }}</span>
        </template>

        <transition name="om-swap" mode="out-in">

            <!-- CREATE / EDIT · form -->
            <div v-if="create || edit" key="form" class="om-body">
                <div class="om-image">
                    <div class="om-image__head">
                        <label class="om-image__label">Imagen representativa</label>

                        <button v-if="Organization.ImageURL || image"
                            type="button"
                            class="om-image__remove"
                            title="Eliminar imagen representativa"
                            @click="DeleteOrganizationImage">
                            <i class="bi bi-x-lg"></i>
                        </button>
                    </div>

                    <input ref="fileInput" type="file" accept="image/*" hidden @change="HandleFileChange">

                    <div class="om-image__picker">
                        <div class="om-avatar" @click="OpenFilePicker">
                            <transition name="om-avatarSwap" mode="out-in">
                                <img :key="avatarSrc" :src="avatarSrc" class="om-avatar__img" alt="">
                            </transition>
                            <span class="om-avatar__overlay"><i class="bi bi-pencil"></i></span>
                        </div>
                    </div>
                </div>

                <div class="om-field">
                    <PretorInput ref="txtOrganization"
                        :label="'Nombre de la organización'"
                        :placeholder="'Nombre de la organización'"
                        :required="true"
                        :validate="/^[\w&?() -.:;ÑÓÍÁÚÉÜ]+$/"
                        :validateMessage="'El nombre solo puede contener letras, números, espacios y los caracteres: &, ?, #, -, (, ), ., ;, :'"
                        :maxLength="255"/>
                </div>

                <!-- Fields only available to superadministrators. -->
                <template v-if="!notSuperadminMode">
                    <div class="om-field">
                        <PretorInput ref="txtPhone"
                            :label="'Número de teléfono'"
                            :placeholder="'Número de teléfono'"
                            :required="false"
                            :validate="/^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/"
                            :validateMessage="'El número de teléfono no es válido'"
                            :maxLength="255"/>
                    </div>

                    <div class="om-field">
                        <PretorInput ref="txtEmail"
                            :label="'Correo electrónico'"
                            :placeholder="'Correo electrónico'"
                            :required="false"
                            :AutoCaps="false"
                            :validate="/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/"
                            :validateMessage="'El correo electrónico no es válido'"
                            :maxLength="255"/>
                    </div>

                    <div class="om-field">
                        <PretorInput ref="txtAddress"
                            :label="'Domicilio'"
                            :placeholder="'Domicilio'"
                            :required="false"
                            :validate="/^[\w&?() -.:;ÑÓÍÁÚÉÜ]+$/"
                            :validateMessage="'El domicilio solo puede contener letras, números, espacios y los caracteres: &, ?, #, -, (, ), ., ;, :'"
                            :maxLength="255"/>
                    </div>

                    <div class="om-field">
                        <PretorInput ref="txtRFC"
                            :label="'RFC'"
                            :placeholder="'RFC'"
                            :required="false"
                            :validate="/^[\w\dñÑ]{12,}$/"
                            :validateMessage="'El RFC solo puede contener letras y números, y debe tener al menos 12 caracteres'"
                            :maxLength="25"/>
                    </div>

                    <div class="om-field om-field--last">
                        <PretorInput ref="txtAdminEmail"
                            :label="'Correo electrónico del administrador'"
                            :placeholder="'Correo electrónico del administrador'"
                            :required="true"
                            :AutoCaps="false"
                            :validate="/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/"
                            :validateMessage="'El correo electrónico no es válido'"
                            :disabled="edit && Organization.CanEditAdmin == 0"
                            :maxLength="255"/>
                    </div>
                </template>

                <PretorFormError class="om-formError" :message="formError"/>
            </div>

            <!-- VIEW · read-only. Each field on its own row. -->
            <div v-else key="view" class="om-body">
                <div class="om-viewImage">
                    <img v-if="Organization.ImageURL" :src="Organization.ImageURL" class="om-avatar__img om-avatar__img--view">
                    <img v-else :src="organizationIcon" class="om-avatar__img om-avatar__img--view">
                </div>

                <div class="om-meta">
                    <div class="om-cell">
                        <span class="om-cell__label">Nombre</span>
                        <span class="om-cell__value">{{ Organization.Organization }}</span>
                    </div>

                    <div class="om-cell">
                        <span class="om-cell__label">Administrador</span>
                        <span class="om-cell__value">{{ Organization.AdminEmail }}</span>
                    </div>

                    <div class="om-cell">
                        <span class="om-cell__label">Estado</span>
                        <span class="om-cell__value">{{ Organization.Status }}</span>
                    </div>

                    <div v-if="Organization.Email != null" class="om-cell">
                        <span class="om-cell__label">Correo electrónico</span>
                        <span class="om-cell__value">{{ Organization.Email }}</span>
                    </div>

                    <div v-if="Organization.PhoneNumber != null" class="om-cell">
                        <span class="om-cell__label">Teléfono</span>
                        <span class="om-cell__value">{{ Organization.PhoneNumber }}</span>
                    </div>

                    <div v-if="Organization.Address != null" class="om-cell">
                        <span class="om-cell__label">Domicilio</span>
                        <span class="om-cell__value">{{ Organization.Address }}</span>
                    </div>

                    <div v-if="Organization.RFC != null" class="om-cell">
                        <span class="om-cell__label">RFC</span>
                        <span class="om-cell__value">{{ Organization.RFC }}</span>
                    </div>

                    <div class="om-cell">
                        <span class="om-cell__label">Creada por</span>
                        <div class="om-user">
                            <span class="om-avatarSm">
                                <img v-if="creatorAvatar.ProfilePictureURL" :src="creatorAvatar.ProfilePictureURL" class="om-avatarSm__img" alt="">
                                <span v-else class="om-avatarSm__img om-avatarSm__initials">{{ creatorAvatar.Initials }}</span>
                            </span>
                            <span class="om-cell__value om-cell__value--audit">{{ Organization.CreatorUser }} el {{ Organization.CreationDate }}</span>
                        </div>
                    </div>

                    <div v-if="Organization.CreationDate != Organization.LastUpdate" class="om-cell">
                        <span class="om-cell__label">Actualizada por</span>
                        <div class="om-user">
                            <span class="om-avatarSm">
                                <img v-if="updaterAvatar.ProfilePictureURL" :src="updaterAvatar.ProfilePictureURL" class="om-avatarSm__img" alt="">
                                <span v-else class="om-avatarSm__img om-avatarSm__initials">{{ updaterAvatar.Initials }}</span>
                            </span>
                            <span class="om-cell__value om-cell__value--audit">{{ Organization.UpdaterUser }} el {{ Organization.LastUpdate }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <template #footer>
            <transition name="om-swap" mode="out-in">
                <div :key="footerKey" class="om-footer">
                    <!-- CREATE -->
                    <template v-if="create">
                        <PretorButton
                            :variant="'outline'"
                            :label="'Cancelar'"
                            :Class="'omFooterBtn'"
                            @click="RequestClose"/>
                        <PretorButton
                            :label="'Registrar organización'"
                            :Class="'omFooterBtn'"
                            @click="CreateOrganizationCaller"/>
                    </template>

                    <!-- EDIT -->
                    <template v-else-if="edit">
                        <PretorButton
                            :variant="'outline'"
                            :label="'Cancelar'"
                            :Class="'omFooterBtn'"
                            @click="CancelEditCaller"/>
                        <PretorButton
                            :label="'Guardar cambios'"
                            :Class="'omFooterBtn'"
                            @click="SaveChangesCaller"/>
                    </template>

                    <!-- VIEW · contextual actions -->
                    <template v-else>
                        <PretorButton v-if="Organization.CanEnable == 1"
                            :icon="'bi bi-unlock-fill'"
                            :label="'Habilitar'"
                            :Class="'omFooterBtn'"
                            @click="EnableOrganizationCaller"/>

                        <PretorButton v-if="Organization.CanDisable == 1"
                            :icon="'bi bi-lock-fill'"
                            :label="'Deshabilitar'"
                            :Class="'omFooterBtn'"
                            @click="DisableOrganizationCaller"/>

                        <PretorButton v-if="Organization.CanResendInvitation == 1"
                            :icon="'bi bi-send'"
                            :label="'Reenviar invitación'"
                            :Class="'omFooterBtn'"
                            @click="ResendInvitationCaller"/>

                        <PretorButton v-if="Organization.CanEdit == 1"
                            :icon="'bi bi-pencil-square'"
                            :label="'Editar'"
                            :Class="'omFooterBtn'"
                            @click="UpdateOrganization"/>
                    </template>
                </div>
            </transition>
        </template>
    </BaseModal>

    <!-- Image cropper — layered on top of the organization modal (declared after
         it so, at the shared z-index, its overlay paints above). -->
    <BaseModal
        :show="imageModalVisible"
        @update:show="OnImageModalUpdateShow"
        :closable="true"
        :dismissible="true"
        :title="'Imagen de la organización'"
        :maxWidth="480">

        <div class="om-cropperBody">
            <div class="om-cropperHint">
                <span class="om-cropperHint__icon"><i class="bi bi-info-circle"></i></span>
                <span>Ajusta el tamaño y la posición de la imagen tomando el marco circular como referencia de cómo se verá en la plataforma.</span>
            </div>

            <Cropper v-if="imageModalVisible" ref="cropper" :id="modalId + '_cropper'"/>
        </div>

        <template #footer>
            <div class="om-cropperFooter">
                <PretorButton
                    :variant="'outline'"
                    :icon="'bi bi-upload'"
                    :label="'Cambiar imagen'"
                    :Class="'omCropperBtn'"
                    @click="OpenFilePicker"/>
                <PretorButton
                    :icon="'bi bi-arrow-right-circle'"
                    :label="'Continuar'"
                    :Class="'omCropperBtn'"
                    @click="SelectImage"/>
            </div>
        </template>
    </BaseModal>

    <!-- Declared after BaseModal so its overlay paints above the modals (discard /
         action confirmations). -->
    <ConfirmationModal ref="confirmationModal" v-if="!store.isCustomer"/>
</template>

<script>
import PretorButton from '../../Generic/Button.vue'
import PretorInput from '../../Generic/Input.vue'
import { useStore } from '@/store/DefaultStore.js';
import Cropper from '@/components/Profile/Cropper.vue';
import BaseModal from '@/components/Generic/BaseModal.vue';
import PretorFormError from '@/components/Generic/FormError.vue';
import UserService from '@/services/UserService.js';
import ShowNotification from '@/services/ToastService.js';
import ErrorService from '@/services/ErrorService.js';
import ConfirmationModal from '@/components/Generic/ConfirmationModal.vue';
import OrganizationService from '@/services/OrganizationService';
import { useSessionStore } from '@/store/SessionStore';
import UserAvatarMixin from '@/mixins/UserAvatarMixin';
import WaitForRefMixin from '@/mixins/WaitForRefMixin';
import OrganizationIcon from '@/images/OrganizationIcon.png';

export default{
    name: 'OrganizationModal',
    mixins: [UserAvatarMixin, WaitForRefMixin],
    components: {
        PretorButton, PretorInput, Cropper, BaseModal, PretorFormError, ConfirmationModal
    },
    props: {
        modalId: {
            type: String,
            required: false,
            default: 'organizationModal'
        }
    },
    setup(){
        const store = useStore();
        const sessionStore = useSessionStore();

        return{
            store, sessionStore
        }
    },
    data(){
        return{
            //BaseModal visibility + inline (controlled) error surface
            visible: false,
            imageModalVisible: false,
            formError: '',

            organizationIcon: OrganizationIcon,

            notSuperadminMode: false,
            PKOrganization: null,
            create: true,
            edit: false,
            view: false,
            image: null,
            imageFile: null,
            selectedFile: null,
            // Creator / updater avatars for the read view's audit rows (photo +
            // initials), resolved from the shared user cache like ProcessLogModal.
            creatorAvatar: { ProfilePictureURL: null, Initials: '', Name: '' },
            updaterAvatar: { ProfilePictureURL: null, Initials: '', Name: '' },
            fields: ['txtOrganization', 'txtPhone', 'txtEmail', 'txtAddress', 'txtRFC', 'txtAdminEmail'],
            fieldsProperties: {
                "txtOrganization" : 'Organization',
                "txtPhone" : 'PhoneNumber',
                "txtEmail" : 'Email',
                "txtAddress" : 'Address',
                "txtRFC" : 'RFC',
                "txtAdminEmail" : 'AdminEmail'
            },
            organizationImageChanged: false,
            Organization: {},
            setMode: {
                create: () => {
                    this.create = true;
                    this.view = false;
                    this.edit = false;
                },
                view: () => {
                    this.create = false;
                    this.view = true;
                    this.edit = false;
                },
                edit: () => {
                    this.create = false;
                    this.view = false;
                    this.edit = true;
                }
            }
        }
    },

    computed: {
        header(){
            if(this.create) return 'Registrando organización';
            if(this.view) return 'Detalles de la organización';
            return 'Editando organización';
        },
        footerKey(){
            if(this.create) return 'create';
            if(this.edit) return 'edit';
            return 'view';
        },
        // Single source for the picker avatar so a newly cropped image can
        // cross-fade in over the previous one via a keyed <transition>.
        avatarSrc(){
            if(this.image) return this.image;
            if(this.Organization.ImageURL) return this.Organization.ImageURL;
            return this.organizationIcon;
        }
    },

    mounted() {
        this.emitter.on('OpenOrganizationModalFromNavbar', () => {
            this.OpenModal(this.store.CurrentOrganizationPK, true);
        });
    },

    beforeUnmount() {
        if (this.image) {
            URL.revokeObjectURL(this.image);
        }

        this.emitter.off('OpenOrganizationModalFromNavbar');
    },

    methods: {
        // Routes BaseModal's close requests (× / backdrop / Escape). While a form
        // is open, closing must confirm the discard first (backdrop/Escape are
        // already gated by :dismissible="view", but the × always fires here).
        OnUpdateShow(show){
            if(show){
                this.visible = true;
                return;
            }
            this.RequestClose();
        },

        RequestClose(){
            if(this.create || this.edit){
                this.$refs.confirmationModal.OpenModal(
                    this.edit
                        ? 'Se descartarán los cambios realizados, ¿deseas cerrar?'
                        : 'Se descartará la información capturada, ¿deseas cerrar?',
                    false,
                    async () => {
                        // Edit from the list returns to the read view; every other
                        // form-close (create, or navbar-edit) closes the modal.
                        if(this.edit && !this.notSuperadminMode){
                            this.formError = '';
                            this.setMode.view();
                        } else {
                            this.CloseModal();
                        }
                    },
                    null
                );
                return;
            }
            this.CloseModal();
        },

        CloseModal(){
            this.visible = false;
        },

        DeleteOrganizationImage(){
            this.image = null;
            this.imageFile = null;
            this.Organization.ImageURL = null;
            this.organizationImageChanged = true;
        },

        // Resolves the creator/updater avatars for the read view. Shows whatever
        // the cache already has (or an initials fallback) immediately, then
        // upgrades to the photo once fetched (ResolveUserData / LoadUsersDataIntoCache
        // come from UserAvatarMixin).
        async LoadOrganizationAvatars(){
            this.creatorAvatar = this.ResolveUserData(this.Organization.FKUserCreator, this.Organization.CreatorUser);
            this.updaterAvatar = this.ResolveUserData(this.Organization.FKUserUpdater, this.Organization.UpdaterUser);

            await this.LoadUsersDataIntoCache([this.Organization.FKUserCreator, this.Organization.FKUserUpdater]);

            this.creatorAvatar = this.ResolveUserData(this.Organization.FKUserCreator, this.Organization.CreatorUser);
            this.updaterAvatar = this.ResolveUserData(this.Organization.FKUserUpdater, this.Organization.UpdaterUser);
        },

        async ResendInvitation(){
            this.store.SetLoader(true);
            try {
                const data = await UserService.ResendInvitation({
                    PKOrganization: this.PKOrganization,
                    PKUser: this.Organization.AdminPKUser
                });
                this.CloseModal();
                this.$parent.GetAllOrganizations();
                ShowNotification.Success(data.message ?? 'La invitación ha sido reenviada exitosamente');
            } catch (error) {
                const inline = ErrorService.Resolve(error, 'Ocurrió un error al reenviar la invitación');
                if(inline){ this.formError = inline; }
                throw error;
            } finally {
                this.store.SetLoader(false);
            }
        },

        ResendInvitationCaller(){
            this.formError = '';
            if(this.Organization.InvitationExpired){
                let message = `Al continuar, se reenviará una invitación de registro al correo <b>${this.Organization.AdminEmail}</b>
                para ser administrador de la misma. Una vez que el usuario se haya registrado <b>no podrá</b> ser cambiado por usted en el futuro, ¿desea continuar?`

                this.$refs.confirmationModal.OpenModal(
                    message,
                    false,
                    this.ResendInvitation,
                    null
                );
            }
            else{
                this.ResendInvitation().catch(() => {});
            }
        },

        EnableOrganizationCaller(){
            this.formError = '';
            let message = this.Organization.CanEditAdmin ? `Al habilitar esta organización el usuario administrador volverá a tener acceso al registro.
                Una vez que el usuario se haya registrado <b>no podrá</b> ser cambiado por usted en el futuro, ¿desea continuar?`
                : `Al habilitar esta organización los usuarios registrados a ella volverán a tener acceso a la misma, ¿desea continuar?`;

            this.$refs.confirmationModal.OpenModal(
                message,
                false,
                this.EnableOrganizaiton,
                null
            );
        },

        async EnableOrganizaiton(){
            this.store.SetLoader(true);
            try {
                await OrganizationService.EnableOrganization({PKOrganization: this.PKOrganization});
                this.CloseModal();
                this.$parent.GetAllOrganizations();
                ShowNotification.Success('La organización ha sido habilitada exitosamente');
            } catch (error) {
                const inline = ErrorService.Resolve(error, 'Ocurrió un error al habilitar la organización');
                if(inline){ this.formError = inline; }
                throw error;
            } finally {
                this.store.SetLoader(false);
            }
        },

        DisableOrganizationCaller(){
            this.formError = '';
            let message = this.Organization.CanEditAdmin ? `Al deshabilitar esta organización la invitación del usuario será cancelada, ¿desea continuar?`
                : `Al deshabilitar esta organización todos los usuarios registrados a ella perderán acceso a la misma, ¿desea continuar?`;

            let logOutMsg = Object.keys(this.store.Organizations).length == 1 ? 'se cerrará tu sesión activa' : 'serás dirigido a tu siguiente organización activa';

            if(this.PKOrganization == this.store.CurrentOrganizationPK)
                message = `Estás deshabilitando la organización en la que te encuentras trabajando.
                Al deshabilitarla todos los usuarios registrados a ella perderán acceso a la misma y ${logOutMsg}, ¿desea continuar?`

            this.$refs.confirmationModal.OpenModal(
                message,
                false,
                this.DisableOrganizaiton,
                null
            );
        },

        async DisableOrganizaiton(){
            this.store.SetLoader(true);
            // Set when we're about to navigate away (org-switch reload or logout
            // redirect) so the finally below doesn't clear the loader early and
            // flash the stale/disabled-org UI while that navigation is in flight.
            let willNavigateAway = false;
            try {
                await OrganizationService.DisableOrganization({PKOrganization: this.PKOrganization});
                this.CloseModal();
                ShowNotification.Success('La organización ha sido deshabilitada exitosamente');

                // Disabling the organization the user is currently in navigates away
                // from this view (org switch reload or logout redirect), so the
                // table here is about to be torn down — refreshing it would race
                // that navigation and hit a null $refs.table. Only refresh in place
                // when some other organization was disabled.
                if(this.PKOrganization == this.store.CurrentOrganizationPK){
                    const nextOrganization = this.store.Organizations.find(x => x.PKOrganization != this.PKOrganization);

                    willNavigateAway = true;

                    if(nextOrganization != null){
                        this.sessionStore.SwitchOrganization(nextOrganization.PKOrganization);
                        this.$router.go(0);
                    }
                    else{
                        await this.sessionStore.LogOut();
                        this.$router.push({name: 'login'});
                    }
                }
                else{
                    this.$parent.GetAllOrganizations();
                }
            } catch (error) {
                const inline = ErrorService.Resolve(error, 'Ocurrió un error al deshabilitar la organización');
                if(inline){ this.formError = inline; }
                throw error;
            } finally {
                if(!willNavigateAway){
                    this.store.SetLoader(false);
                }
            }
        },

        CancelEditCaller(){
            this.$refs.confirmationModal.OpenModal(
                'Se descartarán los cambios realizados, ¿deseas continuar?',
                false,
                this.CancelEdit,
                null
            );
        },

        CancelEdit(){
            this.formError = '';
            if(this.notSuperadminMode){
                this.CloseModal();
                return;
            }

            this.setMode.view();
        },

        async SaveChanges(){
            this.store.SetLoader(true);

            let organization = this.GetOrganizationModel();

            let formData = new FormData();
            if(this.organizationImageChanged){
                formData.append('Image', this.imageFile);
            }

            try {
                await OrganizationService.UpdateOrganization(organization, formData);
                this.CloseModal();

                if(!this.notSuperadminMode){
                    this.$parent.GetAllOrganizations();
                }

                if(this.PKOrganization == this.store.CurrentOrganizationPK)
                    this.ReloadCurrentOrganizationData();

                ShowNotification.Success('La organización ha sido actualizada exitosamente');
            } catch (error) {
                const inline = ErrorService.Resolve(error, 'Ocurrió un error al actualizar la organización');
                if(inline){ this.formError = inline; }
                throw error;
            } finally {
                this.store.SetLoader(false);
            }
        },

        async ReloadCurrentOrganizationData() {
            this.store.SetLoader(true);
            try {
                const data = await OrganizationService.GetOrganizationDetail({ PKOrganization: this.PKOrganization });
                let currentOrganization = this.store.Organizations.find(x => x.PKOrganization == this.PKOrganization);
                currentOrganization.ImageURL = data.Organization.ImageURL;
                currentOrganization.Organization = data.Organization.Organization;
            } catch (error) {
                ShowNotification.Error(error, 'Ocurrió un error al obtener los detalles de la organización');
            }
            this.store.SetLoader(false);
        },

        SaveChangesCaller(){
            this.formError = '';

            // ValidateForm() calls GetValid() on each field ref, which surfaces
            // that field's own inline error below it — no summary toast needed.
            if(!this.ValidateForm()){
                return;
            }

            if(!this.GetCanSaveChanges()){
                this.formError = 'No se han hecho cambios por guardar.';
                return;
            }

            if(this.notSuperadminMode){
                this.SaveChanges().catch(() => {});
                return;
            }

            if(this.Organization.CanEditAdmin && this.$refs.txtAdminEmail.GetValue() != this.Organization.AdminEmail.toUpperCase()){
                let message = `Al cambiar el correo electrónico del administrador, la invitación del administrador original será cancelada y se enviará una
                invitación al nuevo administrador. Una vez que el usuario se haya registrado <b>no podrá</b> ser cambiado por usted en el futuro, ¿desea continuar?`;

                this.$refs.confirmationModal.OpenModal(
                    message,
                    false,
                    this.SaveChanges,
                    null
                );
            }
            else{
                this.SaveChanges().catch(() => {});
            }
        },

        GetCanSaveChanges(){
            let canSaveChanges = false;

            let fields = this.fields;
            if(this.notSuperadminMode)
                fields = ['txtOrganization'];

            fields.forEach(field => {
                if(this.$refs[field].GetValue() != (this.Organization[this.fieldsProperties[field]] == null ? '' : this.Organization[this.fieldsProperties[field]]).toUpperCase()){
                    canSaveChanges = true;
                    return;
                }
            });

            return canSaveChanges || this.organizationImageChanged;
        },

        // View → edit rides an out-in body cross-fade, so the form (and its field
        // refs) only mounts after the read view has finished leaving. Wait for the
        // ref before seeding it — a plain $nextTick fires while the form is still
        // absent, which read as "Cannot read properties of null (reading 'SetValue')".
        async UpdateOrganization(){
            this.formError = '';
            this.setMode.edit();

            await this.WaitForRef('txtOrganization');
            this.ClearModal();
            this.SetOrganizationData();
        },

        SetOrganizationData(){
            this.$refs.txtOrganization.SetValue(this.Organization.Organization);

            if(this.notSuperadminMode)
                return;

            this.$refs.txtPhone.SetValue(this.Organization.PhoneNumber ?? '');
            this.$refs.txtEmail.SetValue(this.Organization.Email ?? '');
            this.$refs.txtAddress.SetValue(this.Organization.Address ?? '');
            this.$refs.txtRFC.SetValue(this.Organization.RFC ?? '');
            this.$refs.txtAdminEmail.SetValue(this.Organization.AdminEmail);
        },

        async OpenModal(PKOrganization = null, fromProfileMenu = false)
        {
            this.notSuperadminMode = fromProfileMenu;
            this.organizationImageChanged = false;
            this.formError = '';
            this.image = null;
            this.imageFile = null;
            this.selectedFile = null;

            if(PKOrganization == null){
                this.Organization = {};
                this.setMode.create();
                this.visible = true;

                await this.WaitForRef('txtOrganization');
                this.ClearModal();

                setTimeout(() => {
                    if(this.$refs.txtOrganization)
                        this.$refs.txtOrganization.SetFocus();
                }, 300)
            }
            else{
                this.PKOrganization = PKOrganization;
                await this.GetOrganizationDetail();

                if(this.notSuperadminMode){
                    this.setMode.edit();
                    this.visible = true;
                    await this.WaitForRef('txtOrganization');
                    this.ClearModal();
                    this.SetOrganizationData();
                }
                else{
                    this.setMode.view();
                    this.visible = true;
                    this.LoadOrganizationAvatars();
                }
            }
        },

        // ---------------- Image cropper ----------------
        OnImageModalUpdateShow(show){
            if(!show){
                this.CloseImageModal();
                return;
            }
            this.imageModalVisible = true;
        },

        CloseImageModal(){
            this.imageModalVisible = false;
            this.selectedFile = null;
            if(this.$refs.fileInput){
                this.$refs.fileInput.value = '';
            }
        },

        OpenFilePicker() {
            this.$refs.fileInput.click();
        },

        async HandleFileChange() {
            let file = this.$refs.fileInput.files[0];

            if(this.selectedFile != null && file == null)
                return;

            if(file == null)
                return;

            if(file.type != 'image/png' && file.type != 'image/jpeg'){
                ShowNotification.Error('El archivo seleccionado no es una imagen válida');
                return;
            }

            this.selectedFile = file;

            const wasOpen = this.imageModalVisible;
            if(!wasOpen){
                this.imageModalVisible = true;
            }

            // The Cropper mounts with the image modal's box (rendered on
            // imageModalVisible); wait until its ref exists before uploading.
            await this.WaitForRef('cropper');
            this.$refs.cropper.uploadImage(this.selectedFile);
        },

        async SelectImage(){
            this.imageFile = await this.$refs.cropper.getCroppedImage();
            if(this.image){
                URL.revokeObjectURL(this.image);
            }
            this.image = URL.createObjectURL(this.imageFile);
            this.organizationImageChanged = true;
            this.CloseImageModal();
        },

        async CheckAdminExists(){
            const Email = this.$refs.txtAdminEmail.GetValue();
            const data = await UserService.GetUserExistsByEmail({Email});
            return data.Exists;
        },

        ValidateForm() {
            let valids = [];

            let fields = this.fields;
            if(this.notSuperadminMode)
                fields = ['txtOrganization'];

            fields.forEach(field => {
                if (this.$refs[field])
                    valids.push(this.$refs[field].GetValid());
            });

            return (valids.includes(false) ? false : true);
        },

        async CreateOrganizationCaller(){
            this.formError = '';

            if(!this.ValidateForm()){
                return;
            }

            this.store.SetLoader(true);
            let userExists;
            try {
                userExists = await this.CheckAdminExists();
            } catch (error) {
                const inline = ErrorService.Resolve(error, 'Ocurrió un error al validar el administrador');
                if(inline){ this.formError = inline; }
                this.store.SetLoader(false);
                return;
            }
            this.store.SetLoader(false);

            let message = userExists ?
            `Al continuar, se creará una organización independiente al resto de la plataforma: el usuario con el correo <b>${this.$refs.txtAdminEmail.GetValue()}</b>
            será asignado como administrador de la misma y <b>no podrá</b> ser cambiado por usted en el futuro, ¿desea continuar?`
            : `Al continuar, se creará una organización independiente al resto de la plataforma: se enviará una invitación de registro al correo <b>${this.$refs.txtAdminEmail.GetValue()}</b>
            para ser administrador de la misma. Una vez que el usuario se haya registrado <b>no podrá</b> ser cambiado por usted en el futuro, ¿desea continuar?`;

            this.$refs.confirmationModal.OpenModal(
                message,
                false,
                this.CreateOrganization,
                null
            );
        },

        async CreateOrganization(){
            this.store.SetLoader(true);

            let organization = this.GetOrganizationModel();

            let formData = new FormData();
            if(this.organizationImageChanged){
                formData.append('Image', this.imageFile);
                organization.Image = formData;
            }

            try {
                await OrganizationService.CreateOrganization(organization, formData);
                this.CloseModal();
                this.$parent.GetAllOrganizations();
                ShowNotification.Success('Organización registrada correctamente');

                try {
                    const data = await UserService.GetUserOrganizations();
                    this.store.SetOrganizations(data.Organizations);
                } catch (error) {
                    ShowNotification.Error(error, 'Ocurrió un error al obtener las organizaciones del usuario');
                }
            } catch (error) {
                const inline = ErrorService.Resolve(error, 'Ocurrió un error al registrar la organización');
                if(inline){ this.formError = inline; }
                throw error;
            } finally {
                this.store.SetLoader(false);
            }
        },

        GetOrganizationModel() {
            if(this.notSuperadminMode)
                return {
                    PKOrganization: this.PKOrganization,
                    Organization: this.$refs.txtOrganization.GetValue(),
                    ImageChanged: this.organizationImageChanged,
                    NotSuperadminMode: true
                }

            return {
                PKOrganization: this.PKOrganization,
                Organization: this.$refs.txtOrganization.GetValue(),
                Email: this.$refs.txtEmail.GetValue(),
                PhoneNumber: this.$refs.txtPhone.GetValue(),
                Address: this.$refs.txtAddress.GetValue(),
                RFC: this.$refs.txtRFC.GetValue(),
                AdminEmail: this.$refs.txtAdminEmail.GetValue(),
                ImageChanged: this.organizationImageChanged,
                NotSuperadminMode: false
            }
        },

        async GetOrganizationDetail() {
            this.store.SetLoader(true);
            try {
                const data = await OrganizationService.GetOrganizationDetail({ PKOrganization: this.PKOrganization });
                this.Organization = data.Organization;
            } catch (error) {
                ShowNotification.Error(error, 'Ocurrió un error al obtener los detalles de la organización');
            }
            this.store.SetLoader(false);
        },

        ClearModal(){
            this.fields.forEach(field => {
                if(this.$refs[field])
                    this.$refs[field].ClearField();
            });

            this.image = null;
            this.selectedFile = null;
        },
    }
}
</script>

<style scoped>
/* Self-contained palette (inlined, matching the BaseModal family / UsersModal):
     ink #1f1f2c · muted #6b6680 · faint #9a94a8 · line #ece8f4
     lavender-soft #f2f4ff · lavender #dee4ff · accent-ink #4b4770
     ease-out cubic-bezier(0.23, 1, 0.32, 1) */

.om-body,
.om-cropperBody {
    --ease-out: cubic-bezier(0.23, 1, 0.32, 1);
}

/* ---------------- Header ---------------- */
.om-head__title {
    flex: 1;
    min-width: 0;
    font-family: 'SwitzerBold', sans-serif;
    font-size: 16px;
    letter-spacing: -0.01em;
    color: #1f1f2c;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ---------------- Body shell ---------------- */
.om-body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 6px 24px 12px;
    scrollbar-width: thin;
    scrollbar-color: rgba(31, 31, 44, 0.18) transparent;
}

.om-body::-webkit-scrollbar {
    width: 8px;
}

.om-body::-webkit-scrollbar-thumb {
    background-color: rgba(31, 31, 44, 0.16);
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;
}

/* ---------------- Image picker ---------------- */
.om-image {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.om-image__head {
    display: flex;
    align-items: center;
}

.om-image__label {
    margin: 0;
    margin-right: auto;
    font-size: 12px;
    color: #6b6680;
}

.om-image__remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #9a94a8;
    font-size: 13px;
    cursor: pointer;
    transition: background-color 160ms ease, color 160ms ease;
}

@media (hover: hover) and (pointer: fine) {
    .om-image__remove:hover {
        background-color: #f2f4ff;
        color: #4b4770;
    }
}

.om-image__picker {
    display: flex;
    justify-content: center;
}

.om-avatar {
    position: relative;
    width: 104px;
    height: 104px;
    border-radius: 50%;
    overflow: hidden;
    cursor: pointer;
}

.om-avatar__img {
    display: block;
    width: 104px;
    height: 104px;
    object-fit: cover;
    border-radius: 50%;
    background-color: #dee4ff;
    box-shadow: 0 1px 2px rgba(31, 31, 44, 0.2);
    transition: filter 200ms var(--ease-out);
}

.om-avatar__overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 22px;
    opacity: 0;
    transition: opacity 200ms var(--ease-out);
    pointer-events: none;
}

.om-avatar__overlay i {
    margin: 0;
}

@media (hover: hover) and (pointer: fine) {
    .om-avatar:hover .om-avatar__img {
        filter: brightness(55%);
    }

    .om-avatar:hover .om-avatar__overlay {
        opacity: 1;
    }
}

/* Avatar image swap — a newly cropped image cross-fades + scales in over the
   previous one (feedback that the crop took). The leaving image is taken out of
   flow so the two overlap in the same 104px circle. */
.om-avatarSwap-enter-active {
    transition: opacity 220ms var(--ease-out), transform 220ms var(--ease-out);
}

.om-avatarSwap-leave-active {
    position: absolute;
    inset: 0;
    transition: opacity 140ms ease;
}

.om-avatarSwap-enter-from {
    opacity: 0;
    transform: scale(0.94);
}

.om-avatarSwap-leave-to {
    opacity: 0;
}

/* ---------------- Form fields ---------------- */
.om-field {
    margin-bottom: 0;
}

.om-field :deep(.pretorInput),
.om-field :deep(.pretorFormWrapper),
.om-field :deep(.pretorForm) {
    margin-bottom: 0;
}

.om-field :deep(.pretorField__label) {
    display: block;
    font-size: 12px;
    color: #6b6680;
}

.om-formError {
    margin: 0 !important;
}

/* Form entrance — the image block and fields settle in in sequence as the form
   enters (stagger, so nothing pops in all at once). nth-of-type counts by
   element (all divs): the image is the 1st div, the fields the 2nd..7th. */
.om-image,
.om-field {
    animation: om-rise 340ms var(--ease-out) both;
}

.om-image { animation-delay: 30ms; }
.om-field:nth-of-type(2) { animation-delay: 70ms; }
.om-field:nth-of-type(3) { animation-delay: 100ms; }
.om-field:nth-of-type(4) { animation-delay: 130ms; }
.om-field:nth-of-type(5) { animation-delay: 160ms; }
.om-field:nth-of-type(6) { animation-delay: 190ms; }
.om-field:nth-of-type(7) { animation-delay: 220ms; }

/* ---------------- View ---------------- */
.om-viewImage {
    display: flex;
    justify-content: center;
    margin-bottom: 2px;
}

.om-avatar__img--view {
    width: 96px;
    height: 96px;
}

/* Each field on its own row. */
.om-meta {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.om-cell {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
}

.om-cell__label {
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 11px;
    letter-spacing: 0.01em;
    color: #9a94a8;
}

.om-cell__value {
    font-size: 15px;
    color: #1f1f2c;
    overflow-wrap: anywhere;
}

.om-cell__value--audit {
    font-size: 13px;
    color: #6b6680;
}

/* Audit-row user (avatar + name/date), mirroring AgreementsModal / ProcessLogModal. */
.om-user {
    display: flex;
    align-items: center;
    gap: 9px;
}

.om-avatarSm {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
}

.om-avatarSm__img {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.om-avatarSm__initials {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #dee4ff;
    color: #4b4770;
    font-size: 11px;
    font-weight: bold;
    box-shadow: none;
}

.om-user .om-cell__value--audit {
    font-size: 13px;
    color: #6b6680;
}

/* View entrance: rows rise in sequence. */
.om-body .om-cell {
    animation: om-rise 360ms var(--ease-out) both;
}

.om-body .om-cell:nth-child(1) { animation-delay: 40ms; }
.om-body .om-cell:nth-child(2) { animation-delay: 70ms; }
.om-body .om-cell:nth-child(3) { animation-delay: 100ms; }
.om-body .om-cell:nth-child(4) { animation-delay: 130ms; }
.om-body .om-cell:nth-child(5) { animation-delay: 160ms; }
.om-body .om-cell:nth-child(6) { animation-delay: 190ms; }
.om-body .om-cell:nth-child(7) { animation-delay: 220ms; }
.om-body .om-cell:nth-child(8) { animation-delay: 250ms; }
.om-body .om-cell:nth-child(9) { animation-delay: 280ms; }

@keyframes om-rise {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: none; }
}

/* ---------------- Footer ---------------- */
.om-footer {
    padding: 14px 24px 18px;
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    align-items: stretch;
}

.om-footer > * {
    flex: 1 1 0;
    min-width: 0;
}

:global(.omFooterBtn) {
    width: 100%;
    margin-top: 0 !important;
}

/* ---------------- Image cropper modal ---------------- */
.om-cropperBody {
    padding: 6px 24px 12px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.om-cropperHint {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 12px;
    background-color: #f2f4ff;
    color: #4b4770;
    font-size: 12.5px;
    line-height: 1.45;
    animation: om-rise 320ms var(--ease-out) both;
    animation-delay: 40ms;
}

.om-cropperHint__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background-color: #dee4ff;
    color: #4b4770;
    font-size: 14px;
}

.om-cropperHint__icon i {
    margin: 0;
}

.om-cropperFooter {
    padding: 14px 24px 18px;
    display: flex;
    gap: 8px;
    align-items: stretch;
}

.om-cropperFooter > * {
    flex: 1 1 0;
    min-width: 0;
}

:global(.omCropperBtn) {
    width: 100%;
    margin-top: 0 !important;
}

@media screen and (max-width: 768px) {
    .om-footer {
        flex-wrap: wrap;
    }

    .om-footer > * {
        flex: 1 1 calc(50% - 4px);
        min-width: calc(50% - 4px);
    }
}

/* ---------------- Mode-change transition ---------------- */
.om-swap-enter-active {
    transition: opacity 220ms cubic-bezier(0.23, 1, 0.32, 1), transform 220ms cubic-bezier(0.23, 1, 0.32, 1);
}

.om-swap-leave-active {
    transition: opacity 150ms ease, transform 150ms ease;
}

.om-swap-enter-from {
    opacity: 0;
    transform: translateY(8px);
}

.om-swap-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

/* ---------------- Reduced motion: keep fades, drop movement ---------------- */
@media (prefers-reduced-motion: reduce) {
    .om-swap-enter-active {
        transition: opacity 160ms ease;
    }

    .om-swap-leave-active {
        transition: opacity 120ms ease;
    }

    .om-swap-enter-from,
    .om-swap-leave-to {
        transform: none;
    }

    .om-avatarSwap-enter-from {
        transform: none;
    }

    .om-image,
    .om-field,
    .om-body .om-cell,
    .om-cropperHint {
        animation: om-fade 260ms ease both;
        animation-delay: 0ms !important;
    }

    @keyframes om-fade {
        from { opacity: 0; }
        to   { opacity: 1; }
    }
}
</style>
